import { AutoComplete } from "primereact/autocomplete";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import React, { useRef, useState } from "react";
import styled from "styled-components";

import { useBulkChangeClaimPayerMutation } from "../../store/queries/claims";
import { useGetAllPayersQuery } from "../../store/queries/payers";
import { IClaim } from "../../types/claim";

interface IBulkEditPayerModalProps {
  visible: boolean;
  onHide: () => void;
  selectedClaims: IClaim[];
  accountId: string;
  /** Called after a successful bulk update so the parent can clear selection / refresh state. */
  onSuccess: () => void;
}

interface IPayerOption {
  name: string;
  id: number | null;
}

// Match the single-claim PayerSelector's "No Match" sentinel.
const NO_MATCH_OPTION: IPayerOption = { name: "No Payer Match", id: null };

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 28rem;
`;

const Hint = styled.div`
  color: #6c757d;
  font-size: 0.875rem;
`;

const ProgressBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0;
`;

/**
 * CLE-2172: confirm-and-apply modal for bulk matched-payer change.
 *
 * Reuses the same payer autocomplete pattern as the single-claim PayerSelector,
 * including the "No Payer Match" sentinel for un-matching claims. After
 * confirmation, calls the bulk endpoint and renders a progress message while
 * waiting (a 25-claim payer change can take tens of seconds because each claim
 * re-prices its child events sequentially).
 */
export function BulkEditPayerModal({
  visible,
  onHide,
  selectedClaims,
  accountId,
  onSuccess,
}: IBulkEditPayerModalProps) {
  const toastRef = useRef<Toast>(null);
  const [searchValue, setSearchValue] = useState<string | IPayerOption>("");
  const [chosenPayer, setChosenPayer] = useState<IPayerOption | null>(null);
  const [filteredPayers, setFilteredPayers] = useState<IPayerOption[]>([]);

  const { data: payersData } = useGetAllPayersQuery();
  const [bulkChangePayer, { isLoading: isApplying }] =
    useBulkChangeClaimPayerMutation();

  const payers: IPayerOption[] = (payersData?.data || payersData || []).map(
    (p: any) => ({ name: p.name, id: p.id })
  );

  const onPayerSearch = (e: any) => {
    const query = e.query.toLowerCase();
    const matchingPayers = payers.filter((payer) =>
      payer.name.toLowerCase().includes(query)
    );
    setFilteredPayers([NO_MATCH_OPTION, ...matchingPayers]);
  };

  const handleApply = async () => {
    if (!chosenPayer) return;

    try {
      const result = await bulkChangePayer({
        claimIds: selectedClaims.map((c) => c.id),
        payerId: chosenPayer.id,
      }).unwrap();

      const isNoMatch = chosenPayer.id === null;
      const verb = isNoMatch ? "marked unmatched" : "updated";
      const detail =
        result.failureCount === 0
          ? `${result.successCount} claim${
              result.successCount === 1 ? "" : "s"
            } ${verb}.`
          : `${result.successCount} ${verb}, ${result.failureCount} failed.`;

      toastRef.current?.show({
        severity: result.failureCount === 0 ? "success" : "warn",
        summary: "Bulk payer update",
        detail,
        life: 6000,
      });

      // Reset local form state but let the parent close the modal +
      // clear selection through onSuccess.
      setSearchValue("");
      setChosenPayer(null);
      onSuccess();
    } catch (err: any) {
      toastRef.current?.show({
        severity: "error",
        summary: "Bulk payer update failed",
        detail:
          err?.data?.error || err?.data?.messages?.[0] || "Request failed.",
        life: 8000,
      });
    }
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="Cancel"
        className="p-button-text"
        onClick={onHide}
        disabled={isApplying}
      />
      <Button
        label={`Apply to ${selectedClaims.length}`}
        icon="pi pi-check"
        onClick={handleApply}
        disabled={!chosenPayer || isApplying}
        loading={isApplying}
      />
    </div>
  );

  return (
    <>
      <Toast ref={toastRef} />
      <Dialog
        header="Update matched payer"
        visible={visible}
        onHide={isApplying ? () => {} : onHide}
        closable={!isApplying}
        footer={footer}
        // Account context is only used to scope the payer list in the future;
        // for now `useGetAllPayersQuery` returns the full set.
        data-account-id={accountId}
      >
        <Body>
          <div>
            Pick a payer to apply to the{" "}
            <strong>{selectedClaims.length}</strong> selected claim
            {selectedClaims.length === 1 ? "" : "s"}. Each claim will be
            reprocessed with the new payer.
          </div>

          <AutoComplete
            value={searchValue}
            suggestions={filteredPayers}
            field="name"
            completeMethod={onPayerSearch}
            onChange={(e) => setSearchValue(e.value)}
            onSelect={(e) => {
              const next = e.value as IPayerOption;
              setSearchValue(next);
              setChosenPayer(next);
            }}
            placeholder="Search payers..."
            dropdown
            disabled={isApplying}
            inputStyle={{ width: "100%" }}
            style={{ width: "100%" }}
          />

          {chosenPayer && chosenPayer.id === null && (
            <Message
              severity="info"
              text="Selected claims will be marked unmatched and excluded from payer analysis."
            />
          )}

          {isApplying && (
            <ProgressBlock>
              <ProgressSpinner
                style={{ width: "24px", height: "24px" }}
                strokeWidth="4"
              />
              <span>
                Reprocessing {selectedClaims.length} claim
                {selectedClaims.length === 1 ? "" : "s"}. This may take a few
                minutes…
              </span>
            </ProgressBlock>
          )}

          {!isApplying && (
            <Hint>
              Each claim re-runs its full pricing pipeline; payer changes can
              take a few seconds per claim.
            </Hint>
          )}
        </Body>
      </Dialog>
    </>
  );
}
